import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';

// Components
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Toggle from 'react-toggle';
import { ButtonV2, Link } from '@zola/zola-ui';
import DragHandle from '../../../common/ui/DragHandle';
import EntityHeader from './EntityHeaderV2';
import EditPageForm from './EditPageCommon/EditPageForm';
import EditPageHeader from './EditPageCommon/EditPageHeader';

// Actions
import { showModal } from '../../../../actions/ModalActions';
import { getRegistries, editRegistry } from '../../../../actions/website/entities/RegistryActions';
import { moveEntity, resetEntities, addEntity } from '../../../../actions/website/EntityActions';
import { togglePage } from '../../../../actions/website/PageActions';
import { fetchRegistry, updateRegistryPublish } from '../../../../actions/RegistryActions';

// Content
import EDIT_PAGES_CONTENT from '../../../../constants/EditPagesContent/EditPagesContent';

// Utils
import { trackRecommendation } from '../../../../util/trackingHelper';
import featureFlag from '../../../../util/featureFlags';

const isTrackWebsiteZolaRegistry = featureFlag.get('isTrackWebsiteZolaRegistry');
const isTrackWebsiteOtherRegistries = featureFlag.get('isTrackWebsiteOtherRegistries');


const isCmsPageFormV2 = featureFlag.get('isCmsPageFormV2');
const PageHeader = isCmsPageFormV2 ? EditPageHeader : EntityHeader;

const Registry = SortableElement(({ registry, onEdit, onDelete }) => {
  const noPropagate = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  return (
    <li className="sortable-registry sortable-item editable-entity" onClick={() => onEdit(registry)}>
      <div className="col-sm-1">
        <DragHandle tooltip="Drag to reorder" />
      </div>
      <div className="col-sm-8">
        <span className="title-v2">{registry.name}</span>
      </div>
      <div className="col-sm-3 entity-actions" onClick={noPropagate}>
        <button onClick={() => onEdit(registry)} title="Edit Registry" className="btn btn-icon with-tooltip no-tooltip-on-mobile" data-tooltip="Edit"><i className="glyphicon glyphicon-pencil" /></button>
        <button onClick={() => onDelete(registry)} title="Delete Registry" className="btn btn-icon with-tooltip no-tooltip-on-mobile" data-tooltip="Delete"><i className="glyphicon glyphicon-trash" /></button>
      </div>
    </li>
  );
});

const PageIntroCopy = hasRegistry => {
  const hasZolaRegistryInfo = <>Let guests easily shop your Zola registry on your website. When ready, <strong>set this page to &quot;Visible&quot;</strong> — and make sure your Zola registry is visible to guests, too.</>;
  const noZolaRegistryInfo = <>Register with us, and your Zola registry will integrate seamlessly with your website. Then guests can shop it right on this page.</>;

  if (hasRegistry) return hasZolaRegistryInfo;

  return noZolaRegistryInfo;
};

Registry.propTypes = {
  registry: PropTypes.shape({}).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  index: PropTypes.number,
};

class Registries extends Component {
  constructor(props) {
    super(props);
    this.state = {
      registryPublished: false,
      callTrackingEvent: false,
      isRegistriesReceived: false,
    };
    this.onToggleRegistryPublish = this.onToggleRegistryPublish.bind(this);
    this.trackWebsiteZolaRegistry = this.trackWebsiteZolaRegistry.bind(this);
    this.trackWebsiteOtherRegistriesViewed = this.trackWebsiteOtherRegistriesViewed.bind(this);
    this.trackWebsiteOtherRegistriesClicked = this.trackWebsiteOtherRegistriesClicked.bind(this);
    this.checkRegistriesReceived = this.checkRegistriesReceived.bind(this);
    this.checkHasOtherRegistries = this.checkHasOtherRegistries.bind(this);
  }

  componentDidMount() {
    const { getRegistry, getRegistryEntities, hasRegistry } = this.props;
    if (hasRegistry) {
      getRegistry().then(() => this.setState({ registryPublished: this.props.registry.is_public }));
    }
    getRegistryEntities(this.checkRegistriesReceived);
  }

  componentWillReceiveProps(nextProps) {
    const { registry } = this.props;
    if (registry.is_public !== nextProps.registry.is_public) {
      this.setState({
        registryPublished: nextProps.registry.is_public
      });
    }
  }

  componentDidUpdate(prevProps) {
    const hasOtherRegistries = this.props.orderedIds.length > 0;
    if (prevProps.orderedIds !== this.props.orderedIds) {
      if (this.state.callTrackingEvent) this.trackWebsiteOtherRegistriesViewed(hasOtherRegistries);
    }
  }

  componentWillUnmount() {
    const { unloadEntities } = this.props;
    unloadEntities();
  }

  onToggleRegistryPublish(e) {
    const { onToggleRegistryPublish, setShippingAddress, registry } = this.props;
    const newValue = e.target.checked;
    if (!registry.has_shipping_address) {
      setShippingAddress();
    } else {
      onToggleRegistryPublish(newValue);
    }
  }

  trackWebsiteZolaRegistry(hasOtherRegistries) {
    const recommendationKey = hasOtherRegistries ? 'website_registry_signup_yes_outside' : 'website_registry_signup_no_outside';
    const recommendationName = hasOtherRegistries ? 'Website Registry Signup Yes Outside' : 'Website Registry Signup No Outside';
    const recommendationCopy = 'Build Your Zola Registry';

    trackRecommendation('Clicked', {
      recommendation_key: recommendationKey,
      recommendation_name: recommendationName,
      recommendation_copy: recommendationCopy,
    });
  }

  trackRecommendationAddRegistry(action) {
    trackRecommendation(action, {
      recommendation_key: 'website_other_registry',
      recommendation_name: 'Website Other Registry',
      recommendation_copy: 'Add A Registry Link',
    });
  }

  trackWebsiteOtherRegistriesViewed(hasOtherRegistries) {
    if (!hasOtherRegistries && isTrackWebsiteOtherRegistries) {
      this.trackRecommendationAddRegistry('Viewed');
      this.setState({ callTrackingEvent: false });
    }
  }

  trackWebsiteOtherRegistriesClicked(hasOtherRegistries) {
    if (!hasOtherRegistries && isTrackWebsiteOtherRegistries) {
      this.trackRecommendationAddRegistry('Clicked');
    }
  }

  checkRegistriesReceived() {
    this.setState({
      isRegistriesReceived: true,
    }, this.checkHasOtherRegistries);
  }

  checkHasOtherRegistries() {
    const { orderedIds } = this.props;
    const hasOtherRegistries = this.state.isRegistriesReceived && orderedIds.length;

    if (!hasOtherRegistries) this.setState({ callTrackingEvent: true });
  }

  render() {
    const { registries, orderedIds, openModal, onEdit, onDelete, page, hasRegistry, slug, themeKey, onPageToggle } = this.props;
    const { registryPublished } = this.state;
    const pageType = page ? page.type : undefined;
    const pageContent = pageType ? EDIT_PAGES_CONTENT[pageType] : {};
    const pageDek = hasRegistry ? pageContent.dek.hasZolaRegistry : pageContent.dek.noZolaRegistry;
    const hasOtherRegistries = orderedIds.length > 0;

    const BuildZolaRegistryCta = (
      <div className="registry-options margin-top">
        <ButtonV2
          component="a"
          role="link"
          href="/wedding/manage/registry"
          onClick={() => {
            if (isTrackWebsiteZolaRegistry) this.trackWebsiteZolaRegistry(hasOtherRegistries)
          }}
        >
          Build Your Zola Registry
        </ButtonV2>
      </div>
    );

    return (
      <div className={`edit-page edit-registry ${hasRegistry ? 'registry-activated' : 'registry-not-activated'}`}>
        {themeKey && (
          <PageHeader onAdd={openModal} page={page} pageIntro={PageIntroCopy(hasRegistry)} pageDek={pageDek} themeKey={themeKey} onPageToggle={onPageToggle} entityOptions={!hasRegistry ? BuildZolaRegistryCta : null}/>
        )}
        {isCmsPageFormV2 && <EditPageForm page={page} />}
        {hasRegistry && (
          <section className="manage-zola-registry">
            <h4 className="registry__title">Zola Registry</h4>
            <div className="toggle-registry-visible">
              <div className="with-tooltip no-tooltip-on-mobile" data-tooltip="Ready to show off your registry? Switch the toggle to &quot;Yes&quot; and anyone will be able to see it. Not ready yet? Keep the toggle on &quot;No.&quot;">
                <span className="toggle__text">Visible to Guests?</span>
                <Toggle
                  checked={registryPublished}
                  onChange={this.onToggleRegistryPublish}
                  className="toggle__button toggle-small toggle-red"
                  icons={{ checked: <span className="toggle-label checked">Yes</span>, unchecked: <span className="toggle-label unchecked">No</span> }}
                />
              </div>
            </div>
            <ButtonV2 component="a" variant="secondary" role="link" href={`/registry/${slug}/edit`} className="manage-zola-registry-btn">Manage Your Registry</ButtonV2>
          </section>
        )}
        {(!hasRegistry && isCmsPageFormV2) && BuildZolaRegistryCta}
        <section className="add-another-registry">
          <p className="add-another-registry__info"><b>Have a registry at another store?</b> Link to it on this page.</p>
          <Link
            arrow
            className="add-another-registry__btn"
            onClick={() => {
              openModal();
              this.trackWebsiteOtherRegistriesClicked(hasOtherRegistries);
            }}>Add A Registry Link</Link>
        </section>
        {!_isEmpty(registries) && (
          <ul className="other-registries sortable-list">
            { orderedIds.map((registryId, index) => <Registry registry={registries[registryId]} onEdit={onEdit} onDelete={onDelete} index={index} key={registryId} />) }
          </ul>
        )}
      </div>
    );
  }
}

Registries.propTypes = {
  registries: PropTypes.shape({}),
  orderedIds: PropTypes.arrayOf(PropTypes.number),
  getRegistry: PropTypes.func.isRequired,
  getRegistryEntities: PropTypes.func.isRequired,
  onToggleRegistryPublish: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  unloadEntities: PropTypes.func.isRequired,
  page: PropTypes.shape({}),
  hasRegistry: PropTypes.bool,
  themeKey: PropTypes.string,
  onPageToggle: PropTypes.func,
  slug: PropTypes.string,
  registry: PropTypes.shape({
    is_public: PropTypes.bool
  }),
  setShippingAddress: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  dashboard: state.dashboard,
  registry: state.registry,
  registries: state.website.entities.byId,
  orderedIds: state.website.entities.orderedIds,
  page: state.website.pages.byId[state.website.pages.typeToId.REGISTRY],
  hasRegistry: state.user.userContext.registry_ids.length > 0,
  themeKey: state.wedding.theme.key,
  slug: state.wedding.wedding.slug,
});

const mapDispatchToProps = dispatch => ({
  onSortEnd: ({ oldIndex, newIndex }) => dispatch(moveEntity(oldIndex, newIndex)),
  openModal: () => {
    dispatch(addEntity());
    dispatch(showModal('REGISTRY', {}, { size: 'md' }));
  },
  getRegistryEntities: (checkRegistriesReceived) => {
    dispatch(getRegistries()).then(checkRegistriesReceived);
  },
  onEdit: (registry) => {
    dispatch(editRegistry(registry));
    dispatch(showModal('REGISTRY', { isUpdate: true }, { size: 'md' }));
  },
  onDelete: entity => dispatch(showModal('DELETE_ENTITY', { entity })),
  unloadEntities: () => dispatch(resetEntities()),
  getRegistry: () => dispatch(fetchRegistry()),
  onPageToggle: pageId => dispatch(togglePage(pageId)),
  onToggleRegistryPublish: (value) => {
    if (value) return dispatch(updateRegistryPublish(value)).then(() => dispatch(showModal('PUBLISH_REGISTRY', {}, { size: 'md' })));
    return dispatch(updateRegistryPublish(value));
  },
  setShippingAddress: () => dispatch(showModal('SET_SHIPPING_ADDRESS', {}, { size: 'md' })),
});

const SortableRegistries = SortableContainer(Registries);

const RegistriesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SortableRegistries);

export default RegistriesContainer;



// WEBPACK FOOTER //
// ./src/components/manage/website/entities/RegistryV2.jsx